import React from "react";
import styled from "styled-components";
import { IonModal } from "@ionic/react";

type Props = {
  isOpen: boolean;
  title: string;
  sections: Record<string, string[]>;
  selectedTags: string[];
  onClose: () => void;
  onToggleTag: (tag: string) => void;
};

const VideoFiltersModal: React.FC<Props> = ({
  isOpen,
  title,
  sections,
  selectedTags,
  onClose,
  onToggleTag,
}) => {
  const selectedCount = selectedTags.length;

  const clearAll = () => {
    selectedTags.forEach((tag) => onToggleTag(tag));
  };

  return (
    <IonModal
      className="resonance-sheetmodal"
      isOpen={isOpen}
      backdropDismiss={true}
      onWillDismiss={onClose}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      canDismiss={true}
    >
      <Sheet>
        <Header>
          <HeaderTitle>{title}</HeaderTitle>
          <CloseButton onClick={onClose} aria-label="Fermer">
            ✕
          </CloseButton>
        </Header>

        <Content>
          {Object.entries(sections).map(([section, tags]) => (
            <Section key={section}>
              <SectionTitle>{section}</SectionTitle>
              <TagList>
                {tags.map((tag) => {
                  const selected = selectedTags.includes(tag);
                  return (
                    <FilterTag
                      key={tag}
                      type="button"
                      onClick={() => onToggleTag(tag)}
                      $selected={selected}
                    >
                      {tag}
                    </FilterTag>
                  );
                })}
              </TagList>
            </Section>
          ))}
        </Content>

        <Footer>
          {selectedCount > 0 && (
            <ClearButton type="button" onClick={clearAll}>
              Tout effacer
            </ClearButton>
          )}
          <ApplyButton type="button" onClick={onClose}>
            {selectedCount > 0
              ? `Appliquer (${selectedCount})`
              : "Appliquer"}
          </ApplyButton>
        </Footer>
      </Sheet>
    </IonModal>
  );
};

export default VideoFiltersModal;

/* -------------------------------------------------------------------------- */
/*                                   STYLES                                   */
/* -------------------------------------------------------------------------- */

const Sheet = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--app-color-background, #ffffff);
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px 12px;
  flex: 0 0 auto;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--app-color-text, #041e32);
`;

const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--app-color-surface-tint, #f5f9fe);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 15px;
  color: var(--app-color-text, #041e32);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 20px 16px;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Section = styled.div`
  margin-bottom: 22px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 12px;
  color: var(--app-color-text, #041e32);
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const FilterTag = styled.button<{ $selected: boolean }>`
  /* Selected chips = attention -> secondary family (see tokens.ts roles). */
  background-color: ${(p) =>
    p.$selected
      ? "var(--app-color-secondary, #FFCE48)"
      : "var(--app-color-surface-tint, #f5f9fe)"};
  color: ${(p) =>
    p.$selected
      ? "var(--app-color-secondary-contrast, #041e32)"
      : "rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55)"};
  font-weight: ${(p) => (p.$selected ? 600 : 500)};
  border: 1.5px solid
    ${(p) => (p.$selected ? "var(--app-color-secondary, #FFCE48)" : "transparent")};
  padding: 9px 16px;
  border-radius: 22px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.18s ease, color 0.18s ease,
    border-color 0.18s ease;

  &:active {
    transform: scale(0.97);
  }
`;

const Footer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px calc(16px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  background: var(--app-color-background, #ffffff);
`;

const ClearButton = styled.button`
  flex: 0 0 auto;
  background: transparent;
  border: none;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 4px;

  &:active {
    opacity: 0.7;
  }
`;

const ApplyButton = styled.button`
  flex: 1;
  /* CTA -> secondary family (see tokens.ts roles). */
  background: var(--app-color-secondary, #ffce48);
  color: var(--app-color-secondary-contrast, #041e32);
  border: none;
  border-radius: 26px;
  padding: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  &:active {
    opacity: 0.85;
  }
`;
